import { useUnit } from 'effector-react';
import { Link, useLocation } from 'react-router-dom';
import { $user } from '@/models/user';
import { GroupsEnum } from '@/types/users';
import usersIcon from '@/assets/svg/menu-users.svg';
import outsourceIcon from '@/assets/svg/menu-outsource.svg';
import manualIcon from '@/assets/svg/menu-manual.svg';
import statsIcon from '@/assets/svg/menu-stats.svg';
import qrIcon from '@/assets/svg/menu-qr.svg';
import employeeIcon from '@/assets/svg/menu-employee.svg';
import { normalizeGroup } from '@/utils/groups';
import { Button } from '@/components/ui/Button';

const menuItems = [
  {
    title: 'Панель сотрудника',
    href: '/',
    targetPath: '/',
    groups: [GroupsEnum.CLIENT],
    icon: employeeIcon,
  },
  {
    title: 'Панель руководителя',
    href: '/store',
    targetPath: '/store',
    groups: [GroupsEnum.GEN_QRCODE],
    icon: qrIcon,
  },
  {
    title: 'ИСИТ',
    href: '/outsourcing',
    targetPath: '/outsourcing',
    groups: [GroupsEnum.OUTSOURCE],
    icon: outsourceIcon,
  },
  {
    title: 'Зарегистрировать исполнителя вручную',
    href: '/manual',
    targetPath: '/manual',
    groups: [GroupsEnum.MANUAL_REG],
    icon: manualIcon,
  },
  {
    title: 'Панель администратора',
    href: '/admin/stores/sessions',
    targetPath: '/admin',
    groups: [GroupsEnum.SESSION_STAT],
    icon: statsIcon,
  },
  {
    title: 'Мои сотрудники',
    href: '/employees',
    targetPath: '/employees',
    groups: [GroupsEnum.EMPLOYEE_LIST],
    icon: usersIcon,
  },
] as const;

const qrPagePaths = new Set(['/outsourcing', '/manual', '/admin', '/store']);

type PortalNavigationProps = {
  onNavigate?: () => void;
  variant?: 'sidebar' | 'page';
  onQrCodeRefresh?: () => void;
};

const PortalNavigation = ({
  onNavigate,
  variant = 'sidebar',
  onQrCodeRefresh,
}: PortalNavigationProps) => {
  const location = useLocation();
  const user = useUnit($user);
  const userGroups = new Set((user?.groups ?? []).map(normalizeGroup));
  const activePath = location.pathname.startsWith('/section')
    ? (new URLSearchParams(location.search).get('next') ?? location.pathname)
    : location.pathname;
  const mobileDevice = /Mobi|Android/i.test(navigator.userAgent);
  const visibleItems = menuItems.filter(
    (item) =>
      item.groups.some((group) => userGroups.has(normalizeGroup(group))) &&
      (variant === 'sidebar' ||
        (qrPagePaths.has(item.targetPath) && (item.targetPath !== '/manual' || mobileDevice))),
  );

  return (
    <nav
      className={
        variant === 'page'
          ? 'flex w-full flex-col gap-3 py-4'
          : 'flex w-full flex-1 flex-col gap-3 overflow-y-auto py-4'
      }
      aria-label="Основное меню"
    >
      {visibleItems.map((item) => {
        const isQrCodeRefresh =
          variant === 'page' && item.targetPath === '/store' && Boolean(onQrCodeRefresh);
        const isActive =
          item.targetPath === '/'
            ? activePath === '/'
            : activePath === item.targetPath || activePath.startsWith(`${item.targetPath}/`);
        const itemClassName =
          variant === 'page'
            ? 'flex w-full shrink-0 items-center justify-center rounded bg-[#631d76] px-4 py-3 text-center font-semibold text-white no-underline transition hover:bg-[#674d81] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#631d76] focus-visible:ring-offset-2'
            : `flex w-full shrink-0 items-center gap-3 px-4 py-3 text-left text-gray-500 no-underline transition hover:bg-gray-100 hover:text-[#631d76] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#631d76] ${isActive ? 'bg-gray-200 text-[#631d76]' : ''}`;
        const itemContent = (
          <>
            {variant === 'sidebar' && (
              <span className="flex h-8 w-8 shrink-0 items-center justify-center">
                <img src={item.icon} alt="" className="h-7 w-7" />
              </span>
            )}
            <span
              className={
                variant === 'page' ? '' : 'min-w-0 flex-1 whitespace-normal text-base leading-5'
              }
            >
              {isQrCodeRefresh ? 'Обновить QR-код' : item.title}
            </span>
          </>
        );

        if (isQrCodeRefresh && onQrCodeRefresh) {
          return (
            <Button
              key={item.title}
              type="button"
              fullWidth
              className={itemClassName}
              onClick={onQrCodeRefresh}
            >
              {itemContent}
            </Button>
          );
        }

        return item.targetPath === '/admin' ? (
          <a
            key={item.title}
            href={item.href}
            className={itemClassName}
            onClick={onNavigate}
            aria-current={isActive ? 'page' : undefined}
          >
            {itemContent}
          </a>
        ) : (
          <Link
            key={item.title}
            to={item.href}
            className={itemClassName}
            onClick={onNavigate}
            aria-current={isActive ? 'page' : undefined}
          >
            {itemContent}
          </Link>
        );
      })}
    </nav>
  );
};

export default PortalNavigation;
